/* Alert routing: which incidents reach somebody, and where — as the design lays it out.
 *
 * **One card says whether every critical incident reaches someone**, kind by kind for all ten kinds
 * the platform raises, because a declared policy can be well-formed, stored, and route nothing
 * critical: a tenant who declared "info volume anomalies to a webhook" and nothing else has silently
 * stopped being paged about a dropped column, and every rule is valid in isolation.
 *
 * **An undeclared policy is the platform's default, and the screen says so rather than listing it as
 * a rule.** The default is not a decision this tenant made; declaring the first rule replaces it
 * entirely, which the form says before the first rule is declared.
 *
 * **Withdrawing asks first**, because withdrawing the last rule returns the tenant to the default and
 * withdrawing any other stops a destination being told about something — and a destination is where
 * a customer's incident metadata leaves the building, so there is no edit: changing one is withdraw
 * and declare again.
 *
 * Declaring, enabling and withdrawing are offered only to a role holding
 * `configure:observability.notification_rule`; everyone else sees the design's role-gated chip.
 */

import { useId, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CONFIGURE_NOTIFICATION_RULES_PERMISSION,
  can,
  useIdentity,
} from '@/entities/session';
import { useIncidentQueue } from '@/features/incidents';
import { INCIDENT_KINDS } from '@/shared/api/generated/observability';
import type {
  IncidentKind,
  NotificationChannel,
  NotificationRule,
  NotificationRuleWrite,
  RoutingPolicy,
  Severity,
} from '@/shared/api/generated/observability';
import { ageText } from '@/shared/model/age';
import { Alert } from '@/shared/ui/Alert';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';

import {
  useDeclareRule,
  useRoutingPolicy,
  useSetRuleEnabled,
  useWithdrawRule,
} from '../api/routing';
import {
  addressesOutside,
  declaredRules,
  destinationOf,
  kindCoverage,
  reaching,
} from '../model/routing';

const SEVERITIES = ['critical', 'warning', 'info'] as const satisfies readonly Severity[];
/* How many recent incidents the replay shows, as the design does. */
const REPLAY_LIMIT = 6;

export function AlertRoutingScreen(): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const mayConfigure = can(identity.data, CONFIGURE_NOTIFICATION_RULES_PERMISSION);
  const policy = useRoutingPolicy();
  const withdraw = useWithdrawRule();
  const setEnabled = useSetRuleEnabled();
  const [formOpen, setFormOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState<NotificationRule | null>(null);
  /* What the last action did, for the live region — the design's toast, said where a screen reader
     hears it. */
  const [said, setSaid] = useState('');

  const header = (
    <PageHeader
      title={t('routing.title')}
      lede={t('routing.subtitle')}
      actions={
        mayConfigure ? (
          <button
            type="button"
            className="lq-btn lq-btn--primary"
            onClick={() => {
              setFormOpen(true);
            }}
          >
            {t('routing.form.open')}
          </button>
        ) : (
          <span
            className="lq-gated"
            title={t('routing.gatedTitle', {
              permission: CONFIGURE_NOTIFICATION_RULES_PERMISSION,
            })}
          >
            {t('routing.gated')}
          </span>
        )
      }
    />
  );

  if (policy.isPending) {
    return (
      <>
        {header}
        <span
          className="lq-skeleton__bar lq-rt__loading"
          role="status"
          aria-label={t('routing.loading')}
        />
      </>
    );
  }

  if (policy.isError) {
    return (
      <>
        {header}
        <Alert tone="error" title={t('routing.readFailed')}>
          {policy.error.message}
        </Alert>
      </>
    );
  }

  const rules = declaredRules(policy.data);
  const enabledCount = rules.filter((rule) => rule.enabled).length;
  const failure = withdraw.error ?? setEnabled.error;

  return (
    <div className="lq-rt">
      {header}

      <Coverage policy={policy.data} enabled={enabledCount} total={rules.length} />

      {formOpen ? (
        <DeclareForm
          first={!policy.data.declared}
          allowedDomain={policy.data.allowed_email_domain}
          onClose={() => {
            setFormOpen(false);
          }}
          onDeclared={() => {
            setSaid(
              t(
                policy.data.declared
                  ? 'routing.msg.declared'
                  : 'routing.msg.firstDeclared',
              ),
            );
            setFormOpen(false);
          }}
        />
      ) : null}

      {failure !== null ? (
        <Alert tone="error" title={t('routing.actionFailed')}>
          {failure.message}
        </Alert>
      ) : null}

      <section className="lq-rt__card lq-rt__card--table" aria-labelledby="routing-rules">
        <h2 id="routing-rules" className="lq-rt__cardTitle">
          {t('routing.section.rules')}
        </h2>
        <div className="lq-rt__scroll">
          <table className="lq-rt__table lq-rt__table--rules">
            <thead>
              <tr>
                <th scope="col">{t('routing.column.channel')}</th>
                <th scope="col">{t('routing.column.kind')}</th>
                <th scope="col">{t('routing.column.severity')}</th>
                <th scope="col">{t('routing.column.destination')}</th>
                <th scope="col">{t('routing.column.state')}</th>
                <th scope="col">
                  <span className="lq-visually-hidden">
                    {t('routing.column.actions')}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => {
                const busy =
                  (withdraw.isPending && withdraw.variables === rule.id) ||
                  (setEnabled.isPending && setEnabled.variables.ruleId === rule.id);
                return (
                  <tr key={rule.id}>
                    <td className="lq-rt__channel">
                      {t(`routing.channel.${rule.channel}`)}
                    </td>
                    <td>
                      {rule.incident_kind === null
                        ? t('routing.form.everyKindLabel')
                        : t(`incidents.kind.${rule.incident_kind}`)}
                    </td>
                    <td className="lq-rt__dim">
                      {t(`routing.severity.${rule.min_severity}`)}
                    </td>
                    <td className="lq-rt__dest" title={destinationOf(rule)}>
                      {destinationOf(rule)}
                    </td>
                    <td
                      className="lq-rt__state"
                      data-on={rule.enabled ? 'true' : undefined}
                    >
                      {t(
                        rule.enabled ? 'routing.state.enabled' : 'routing.state.disabled',
                      )}
                    </td>
                    <td className="lq-rt__actions">
                      {mayConfigure ? (
                        <>
                          <button
                            type="button"
                            className="lq-btn lq-btn--secondary lq-btn--compact"
                            disabled={busy}
                            onClick={() => {
                              setEnabled.mutate(
                                { ruleId: rule.id, enabled: !rule.enabled },
                                {
                                  onSuccess: () => {
                                    setSaid(
                                      t(
                                        rule.enabled
                                          ? 'routing.msg.disabled'
                                          : 'routing.msg.enabled',
                                      ),
                                    );
                                  },
                                },
                              );
                            }}
                          >
                            {t(
                              rule.enabled
                                ? 'routing.action.disable'
                                : 'routing.action.enable',
                            )}
                          </button>
                          <button
                            type="button"
                            className="lq-btn lq-btn--secondary lq-btn--compact"
                            disabled={busy}
                            onClick={() => {
                              setWithdrawing(rule);
                            }}
                          >
                            {t('routing.action.withdraw')}
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rules.length === 0 ? <p className="lq-rt__none">{t('routing.none')}</p> : null}
      </section>

      <Replay policy={policy.data} />

      <ConfirmDialog
        open={withdrawing !== null}
        title={t('routing.withdraw.title')}
        message={
          withdrawing === null
            ? ''
            : rules.length === 1
              ? t('routing.withdraw.last')
              : t('routing.withdraw.body', {
                  channel: t(`routing.channel.${withdrawing.channel}`),
                  destination: destinationOf(withdrawing),
                  kind:
                    withdrawing.incident_kind === null
                      ? t('routing.withdraw.anyKind')
                      : t(`incidents.kind.${withdrawing.incident_kind}`),
                })
        }
        confirmLabel={t('routing.withdraw.confirm')}
        cancelLabel={t('common.cancel')}
        tone="danger"
        busy={withdraw.isPending}
        error={withdraw.isError ? withdraw.error.message : undefined}
        onConfirm={() => {
          if (withdrawing === null) return;
          const last = rules.length === 1;
          withdraw.mutate(withdrawing.id, {
            onSuccess: () => {
              setSaid(t(last ? 'routing.msg.withdrawnLast' : 'routing.msg.withdrawn'));
              setWithdrawing(null);
            },
          });
        }}
        onCancel={() => {
          setWithdrawing(null);
          withdraw.reset();
        }}
      />

      <p className="lq-visually-hidden" role="status">
        {said}
      </p>
    </div>
  );
}

/* Whether critical incidents reach someone, kind by kind. */
function Coverage({
  policy,
  enabled,
  total,
}: {
  readonly policy: RoutingPolicy;
  readonly enabled: number;
  readonly total: number;
}): ReactElement {
  const { t } = useTranslation();
  const tiles = kindCoverage(policy, INCIDENT_KINDS);
  const gaps = tiles.filter((tile) => tile.state === 'nobody').length;
  const tone = !policy.declared ? 'neutral' : gaps > 0 ? 'error' : 'ok';

  return (
    <section className="lq-rt__card lq-rt__coverage" aria-labelledby="routing-coverage">
      <div>
        <h2 id="routing-coverage" className="lq-rt__head" data-tone={tone}>
          {!policy.declared
            ? t('routing.coverage.default')
            : gaps > 0
              ? t('routing.coverage.gaps', { count: gaps })
              : t('routing.coverage.covered')}
        </h2>
        <p className="lq-rt__sub">
          {!policy.declared
            ? t('routing.coverage.defaultSub')
            : t('routing.coverage.enabled', { enabled, total })}
        </p>
      </div>
      <ul className="lq-rt__tiles">
        {tiles.map((tile) => (
          <li key={tile.kind} className="lq-rt__tile" data-state={tile.state}>
            <span className="lq-rt__mark" aria-hidden="true">
              {tile.state === 'covered' ? '✓' : tile.state === 'nobody' ? '!' : '·'}
            </span>
            <span className="lq-rt__kind">{t(`incidents.kind.${tile.kind}`)}</span>
            <span className="lq-rt__reach">
              {tile.state === 'covered'
                ? t('routing.coverage.rules', { count: tile.rules })
                : tile.state === 'nobody'
                  ? t('routing.coverage.nobody')
                  : t('routing.coverage.platformDefault')}
            </span>
          </li>
        ))}
      </ul>
      <p className="lq-rt__foot">{t('routing.coverage.foot')}</p>
    </section>
  );
}

/* Declaring a rule: a channel, the kind and severity it covers, and where it goes. */
function DeclareForm({
  first,
  allowedDomain,
  onClose,
  onDeclared,
}: {
  /** Whether this is the tenant's first rule, which replaces the platform default entirely. */
  readonly first: boolean;
  /** The one mail domain this tenancy may route to, or `null` when none is on record. */
  readonly allowedDomain: string | null;
  readonly onClose: () => void;
  readonly onDeclared: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const declare = useDeclareRule();
  const [channel, setChannel] = useState<NotificationChannel>('email');
  const [kind, setKind] = useState('');
  const [severity, setSeverity] = useState<Severity>('critical');
  const [to, setTo] = useState('');
  const [error, setError] = useState('');
  const toId = useId();
  const hintId = useId();

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const value = to.trim();
    /* Checked here so a reader is told before the round trip; the service is the enforcement, and
       it reads the domain from the tenant record rather than from anything a browser sent. */
    if (value === '') {
      setError(
        t(
          channel === 'email' ? 'routing.form.needRecipient' : 'routing.form.needWebhook',
        ),
      );
      return;
    }
    const recipients = value
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    if (channel === 'email') {
      if (allowedDomain === null) {
        setError(t('routing.form.noDomain'));
        return;
      }
      if (addressesOutside(allowedDomain, recipients).length > 0) {
        setError(t('routing.form.outsideDomain', { domain: allowedDomain }));
        return;
      }
    } else if (!/^https:\/\/\S+$/.test(value)) {
      setError(t('routing.form.needHttps'));
      return;
    }
    setError('');
    const base = {
      channel,
      min_severity: severity,
      ...(kind === '' ? {} : { incident_kind: kind as IncidentKind }),
    };
    const write: NotificationRuleWrite =
      channel === 'email'
        ? { ...base, target_kind: 'email_recipients', recipients }
        : { ...base, target_kind: 'webhook_url', webhook_url: value };
    declare.mutate(write, { onSuccess: onDeclared });
  };

  return (
    <form
      className="lq-rt__card lq-rt__form"
      onSubmit={submit}
      aria-labelledby="routing-form"
    >
      <h2 id="routing-form" className="lq-rt__formTitle">
        {t('routing.form.open')}
      </h2>
      {first ? <p className="lq-rt__first">{t('routing.form.first')}</p> : null}
      <div className="lq-rt__fields">
        <div className="lq-rt__field">
          <span className="lq-rt__label">{t('routing.form.channel')}</span>
          <SegmentedFilter
            variant="track"
            label={t('routing.form.channel')}
            options={[
              { id: 'email' as const, label: t('routing.channel.email') },
              { id: 'webhook' as const, label: t('routing.channel.webhook') },
            ]}
            value={channel}
            onChange={(next) => {
              setChannel(next);
              setTo('');
              setError('');
            }}
          />
        </div>
        <label className="lq-rt__field">
          <span className="lq-rt__label">{t('routing.form.kind')}</span>
          <select
            className="lq-input"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value);
            }}
          >
            <option value="">{t('routing.form.everyKindLabel')}</option>
            {INCIDENT_KINDS.map((each) => (
              <option key={each} value={each}>
                {t(`incidents.kind.${each}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="lq-rt__field">
          <span className="lq-rt__label">{t('routing.form.severity')}</span>
          <select
            className="lq-input"
            value={severity}
            onChange={(event) => {
              setSeverity(event.target.value as Severity);
            }}
          >
            {SEVERITIES.map((each) => (
              <option key={each} value={each}>
                {t(`routing.severity.${each}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="lq-rt__field">
        <label className="lq-rt__label" htmlFor={toId}>
          {t(channel === 'email' ? 'routing.form.recipients' : 'routing.form.webhook')}
        </label>
        <input
          id={toId}
          className="lq-input"
          value={to}
          spellCheck={false}
          aria-describedby={hintId}
          aria-invalid={error === '' ? undefined : true}
          onChange={(event) => {
            setTo(event.target.value);
          }}
        />
        <span id={hintId} className="lq-rt__hint">
          {channel === 'webhook'
            ? t('routing.form.webhookHint')
            : allowedDomain === null
              ? t('routing.form.noDomain')
              : t('routing.form.recipientsHint', { domain: allowedDomain })}
        </span>
      </div>
      {error !== '' ? (
        <p className="lq-rt__error" role="alert">
          {error}
        </p>
      ) : null}
      {declare.isError ? (
        <p className="lq-rt__error" role="alert">
          {declare.error.message}
        </p>
      ) : null}
      <div className="lq-rt__buttons">
        <button type="button" className="lq-btn lq-btn--secondary" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="lq-btn lq-btn--primary"
          disabled={declare.isPending}
        >
          {t('routing.form.submit')}
        </button>
      </div>
    </form>
  );
}

/* The six most recent incidents, as these rules would have routed them. */
function Replay({ policy }: { readonly policy: RoutingPolicy }): ReactElement {
  const { t } = useTranslation();
  const queue = useIncidentQueue();
  const now = new Date();

  return (
    <section className="lq-rt__card lq-rt__card--table" aria-labelledby="routing-replay">
      <h2 id="routing-replay" className="lq-rt__cardTitle">
        {t('routing.section.replay')}
      </h2>
      {queue.isPending ? (
        <span className="lq-skeleton__bar lq-rt__replayLoading" aria-hidden="true" />
      ) : queue.isError ? (
        /* A failed read is not "these rules would reach nobody". */
        <p className="lq-rt__none">{t('routing.replay.unreadable')}</p>
      ) : queue.data.length === 0 ? (
        <p className="lq-rt__none">{t('routing.replay.empty')}</p>
      ) : (
        <div className="lq-rt__scroll">
          <table className="lq-rt__table lq-rt__table--replay">
            <thead>
              <tr>
                <th scope="col">{t('routing.replay.column.incident')}</th>
                <th scope="col">{t('routing.replay.column.severity')}</th>
                <th scope="col">{t('routing.replay.column.opened')}</th>
                <th scope="col">{t('routing.replay.column.reach')}</th>
              </tr>
            </thead>
            <tbody>
              {queue.data.slice(0, REPLAY_LIMIT).map((incident) => {
                const hit = [
                  ...new Set(
                    reaching(policy, incident.kind, incident.severity).map((rule) =>
                      destinationOf(rule),
                    ),
                  ),
                ];
                const reach = !policy.declared
                  ? 'default'
                  : hit.length === 0
                    ? 'nobody'
                    : 'some';
                return (
                  <tr key={incident.id}>
                    <td>{t(`incidents.kind.${incident.kind}`)}</td>
                    <td className="lq-rt__sev" data-severity={incident.severity}>
                      {t(`routing.severityName.${incident.severity}`)}
                    </td>
                    <td className="lq-rt__when">
                      {ageText(t, incident.opened_at, now) ?? incident.opened_at}
                    </td>
                    <td className="lq-rt__to" data-reach={reach}>
                      {reach === 'default'
                        ? t('routing.coverage.platformDefault')
                        : reach === 'nobody'
                          ? t('routing.nobody')
                          : hit.join(', ')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
